import { css } from '@emotion/react';
import { useAtomValue } from 'jotai';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { Button, ButtonProps, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useCallback } from 'react';

import { Breakpoint, ContainerName } from '../../../../../../constants/styles';
import { COMPANY_VIEW_TYPE } from '../../../../../../data-models/view-config.data-model';
import { useFundSelector } from '../../../../../../pages/PortfolioOverview/hooks/useFundSelector';
import { useUpdateSelectedView } from '../../../../../../pages/PortfolioOverview/hooks/useViewActions';
import {
  portfolioFundOptionsStateJ,
  includeNonParticipatingRoundsStateJ,
  selectedFundsAsOptionsPFStateJ,
} from '../../../../../../pages/PortfolioOverview/state/FundFilterStateJ';
import { selectedViewPFJ } from '../../../../../../pages/PortfolioOverview/state/ViewStateJ';
import { SelectItem } from '../../../../../../types';
import { IPopupMenuProps, PopupMenu } from '../../../../../PopupMenu/PopupMenu';

const FundLabelCss = css`
  @container ${ContainerName.headerContainer} (max-width: ${Breakpoint.small}) {
    font-size: 0.825rem;
  }
`;

interface IFundFilterPF2Props {
  ActionComponent?: IPopupMenuProps['ActionComponent'];
}
export function FundFilterPF2({ ActionComponent }: IFundFilterPF2Props) {
  const { onChangeSelection, onSelectAll } = useFundSelector();
  const fundOptions = useAtomValue(portfolioFundOptionsStateJ);
  const selected = useAtomValue(selectedFundsAsOptionsPFStateJ);
  const view = useAtomValue(selectedViewPFJ);
  const includeNonParticipatingRounds = useAtomValue(includeNonParticipatingRoundsStateJ);
  const updateSelectedView = useUpdateSelectedView();

  const isRoundTracker = view?.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER;

  const label = (
    <Typography
      css={FundLabelCss}
      variant='body2'
    >{`Fund ${selected.length}/${fundOptions.length}`}</Typography>
  );

  const _onChange = useCallback(
    (value: SelectItem[]) => {
      if (value.length === fundOptions.length) {
        onSelectAll();
        return;
      }
      onChangeSelection(value);
    },
    [fundOptions.length, onChangeSelection, onSelectAll]
  );

  const handleToggleIncludeNonParticipating = useCallback(() => {
    updateSelectedView({ includeNonParticipatingRounds: !includeNonParticipatingRounds });
  }, [includeNonParticipatingRounds, updateSelectedView]);

  const nonParticipatingToggle = isRoundTracker ? (
    <FormControlLabel
      label={<Typography variant='body2'>Include Non Participating</Typography>}
      sx={{ display: 'flex', ml: '0.1rem', my: '0.25rem' }}
      control={
        <Checkbox
          size='small'
          checked={includeNonParticipatingRounds}
          onChange={handleToggleIncludeNonParticipating}
        />
      }
    />
  ) : undefined;

  return (
    <PopupMenu
      items={fundOptions}
      groupable
      searchable
      label={label}
      onChange={_onChange}
      selectedItems={selected}
      ActionComponent={ActionComponent ?? FundSelectorButton}
      footerContent={nonParticipatingToggle}
    />
  );
}

function FundSelectorButton({ children, ...rest }: ButtonProps) {
  return (
    <Button
      endIcon={<FilterListOutlinedIcon />}
      size='large'
      title='Filter by fund'
      variant='outlined'
      sx={{ height: '2.5rem', mt: '0.15rem' }}
      {...rest}
    >
      {children}
    </Button>
  );
}
